import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Typography, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useServiceNationUrl } from '../../../common/utils/useServiceNationUrl';
import { ServiceNationIframeSkeleton } from './ServiceNationIframeSkeleton';

// Height of the top navigation header, the page fills the rest of the viewport
const HEADER_HEIGHT = 90;

// How long a page can stay open before its SSO URL is regenerated and the iframe reloaded
const SESSION_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

interface ServiceNationIframePageProps {
  // Service Nation page to embed, the `destination` of the SSO URL
  destination: string;
  // Prefix of the page's translation keys: `<key>.heading`, `<key>.subtitle`, `<key>.loadError`
  translationKey: string;
  testIds: { heading: string; iframe: string; error: string; skeleton: string };
}

// Embeds a Service Nation page, authenticated with an SSO token, instead of opening it
// in a new tab.
export function ServiceNationIframePage({
  destination,
  translationKey,
  testIds,
}: ServiceNationIframePageProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { buildServiceNationUrl } = useServiceNationUrl();
  const [iframeUrl, setIframeUrl] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  // The SSO token is generated once per visit, so React StrictMode's double effect
  // run must not request a second one.
  const hasRequestedUrl = useRef(false);
  // The refresh timer outlives renders, so it calls the latest builder through a ref
  const buildServiceNationUrlRef = useRef(buildServiceNationUrl);
  buildServiceNationUrlRef.current = buildServiceNationUrl;

  useEffect(() => {
    if (hasRequestedUrl.current) return;
    hasRequestedUrl.current = true;

    buildServiceNationUrl(destination, true)
      .then(setIframeUrl)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to generate Service Nation SSO token', error);
        setHasError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The session behind the SSO URL expires, so a page left open is reloaded with a new URL
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      buildServiceNationUrlRef.current(destination, true)
        .then((url) => {
          // Show the skeleton again while the new URL loads
          setIsIframeLoaded(false);
          // Recover from a failed first load, which left the error alert up
          setHasError(false);
          setIframeUrl(url);
        })
        .catch((error) => {
          // Keep the current page, the next interval tries again
          // eslint-disable-next-line no-console
          console.error('Failed to refresh Service Nation SSO token', error);
        });
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshTimer);
  }, [destination]);

  const renderContent = () => {
    if (hasError) {
      return (
        <Alert severity="error" data-testid={testIds.error}>
          {t(`${translationKey}.loadError`)}
        </Alert>
      );
    }

    // The iframe loads while hidden so the skeleton stays up until the page is ready
    return (
      <>
        {!isIframeLoaded && (
          <ServiceNationIframeSkeleton testId={testIds.skeleton} showHeading={false} />
        )}
        {iframeUrl && (
          <Box
            component="iframe"
            src={iframeUrl}
            onLoad={() => setIsIframeLoaded(true)}
            title={t(`${translationKey}.heading`)}
            data-testid={testIds.iframe}
            allow="autoplay; fullscreen; clipboard-write"
            sx={{
              display: isIframeLoaded ? 'block' : 'none',
              flex: 1,
              width: '100%',
              minHeight: 600,
              border: 0,
            }}
          />
        )}
      </>
    );
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: `${theme.spacing(3.5)} ${theme.spacing(4)}`,
        // On mobile the top-nav layout's container already pads the page.
        [theme.breakpoints.down('sm')]: { padding: `${theme.spacing(2)} 0` },
      }}>
        <Typography
          component="h1"
          data-testid={testIds.heading}
          sx={{ fontSize: 24, fontWeight: 500, color: theme.palette.text.primary }}
        >
          {t(`${translationKey}.heading`)}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
          {t(`${translationKey}.subtitle`)}
        </Typography>
      </Box>
      {renderContent()}
    </Box>
  );
}
